import { DEFAULT_BRIDGE_TIMEOUT, READY_CHECK_TIMEOUT } from "../constants";
import { BridgeError, TimeoutError } from "../errors";
import { LRUCache } from "../utils/lru-cache";
import {
  type Command,
  CommandType,
  createCommandMessage,
  EventType,
  isEvent,
  isResponse,
} from "./protocol";
import type { BridgeMessage, BridgeRequest, BridgeResponse } from "./types";

/** Used when targetOrigin is not configured and iframe src cannot be parsed. */
const DEFAULT_POST_MESSAGE_TARGET = "*";

export interface PostMessageBridgeOptions {
  timeout?: number;
  onEvent?: (event: BridgeMessage) => void;
  /** Widget iframe origin for postMessage targetOrigin validation. */
  targetOrigin?: string;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

export class PostMessageBridge {
  private readonly timeout: number;
  private messageQueue: BridgeRequest[] = [];
  private readonly pendingRequests: Map<string, PendingRequest> = new Map();
  private messageHandler?: (event: MessageEvent) => void;
  private isReady = false;
  private readonly onEvent?: (event: BridgeMessage) => void;
  private iframe?: HTMLIFrameElement;
  private readonly processedMessageIds: LRUCache<string>;
  private readonly configuredTargetOrigin?: string;
  private widgetTargetOrigin = DEFAULT_POST_MESSAGE_TARGET;

  get ready(): boolean {
    return this.isReady;
  }

  constructor(options: PostMessageBridgeOptions) {
    this.timeout = options.timeout || DEFAULT_BRIDGE_TIMEOUT;
    this.onEvent = options.onEvent;
    this.configuredTargetOrigin = options.targetOrigin;
    this.processedMessageIds = new LRUCache(1000);
  }

  init(iframe: HTMLIFrameElement): void {
    this.iframe = iframe;
    this.widgetTargetOrigin =
      this.configuredTargetOrigin ?? this.resolveWidgetOriginFromIframe(iframe);

    this.messageHandler = (event: MessageEvent) => {
      if (!this.isValidMessageSource(event)) {
        return;
      }

      const message = this.validateMessage(event.data);
      if (!message) {
        console.warn("[Bridge] Invalid message format", { data: event.data });
        return;
      }

      this.processMessage(message);
    };

    window.addEventListener("message", this.messageHandler);
    this.waitForReady(iframe);
  }

  private resolveWidgetOriginFromIframe(iframe: HTMLIFrameElement): string {
    try {
      const src = iframe.src || iframe.getAttribute("src");
      if (src) {
        return new URL(src, window.location.href).origin;
      }
    } catch {
      // fall through
    }
    return DEFAULT_POST_MESSAGE_TARGET;
  }

  private isValidMessageSource(event: MessageEvent): boolean {
    if (
      this.widgetTargetOrigin !== DEFAULT_POST_MESSAGE_TARGET &&
      event.origin !== this.widgetTargetOrigin
    ) {
      return false;
    }

    if (event.source === window || event.source === window.self) {
      const messageId = event.data?.id;
      if (messageId && !this.processedMessageIds.has(`self-${messageId}`)) {
        this.processedMessageIds.add(`self-${messageId}`);
      }
      return false;
    }

    if (
      this.iframe?.contentWindow &&
      event.source !== this.iframe.contentWindow
    ) {
      const messageId = event.data?.id;
      if (
        messageId &&
        !this.processedMessageIds.has(`not-iframe-${messageId}`)
      ) {
        this.processedMessageIds.add(`not-iframe-${messageId}`);
      }
      return false;
    }

    return true;
  }

  private processMessage(message: BridgeMessage): void {
    if (this.processedMessageIds.has(message.id)) {
      return;
    }
    this.processedMessageIds.add(message.id);

    if (isEvent(message)) {
      this.handleEvent(message);
      return;
    }

    if (isResponse(message) && message.requestId) {
      this.handleResponse(message);
      return;
    }
  }

  private validateMessage(data: unknown): BridgeMessage | null {
    if (!data || typeof data !== "object") {
      return null;
    }

    const msg = data as Record<string, unknown>;

    if (
      typeof msg.id !== "string" ||
      typeof msg.type !== "string" ||
      typeof msg.timestamp !== "number"
    ) {
      return null;
    }

    if (
      msg.type === "RESPONSE" &&
      (typeof msg.requestId !== "string" || typeof msg.success !== "boolean")
    ) {
      return null;
    }

    return msg as unknown as BridgeMessage;
  }

  sendCommand(command: Command): Promise<unknown> {
    const message = createCommandMessage(command);

    if (!this.isReady) {
      this.messageQueue.push(message);
      return Promise.resolve();
    }

    return this.sendMessage(message);
  }

  private sendMessage(message: BridgeRequest): Promise<unknown> {
    if (!this.iframe?.contentWindow) {
      return Promise.reject(
        new BridgeError("Widget iframe not found", "IFRAME_NOT_FOUND")
      );
    }

    if (
      this.iframe.contentWindow === window ||
      this.iframe.contentWindow === window.self
    ) {
      return Promise.reject(
        new BridgeError("Cannot send message to self", "SELF_MESSAGE_PREVENTED")
      );
    }

    if (!message.expectsResponse) {
      this.iframe.contentWindow.postMessage(message, this.widgetTargetOrigin);
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRequests.delete(message.id);
        reject(
          new TimeoutError(
            `Bridge request timeout: ${message.type}`,
            "BRIDGE_TIMEOUT"
          )
        );
      }, this.timeout);

      this.pendingRequests.set(message.id, {
        resolve,
        reject,
        timeout: timeoutId,
      });

      const contentWindow = this.iframe?.contentWindow;
      if (!contentWindow) {
        this.pendingRequests.delete(message.id);
        clearTimeout(timeoutId);
        reject(new BridgeError("Widget iframe not found", "IFRAME_NOT_FOUND"));
        return;
      }

      contentWindow.postMessage(message, this.widgetTargetOrigin);
    });
  }

  private handleResponse(response: BridgeResponse): void {
    const pending = this.pendingRequests.get(response.requestId);
    if (!pending) {
      return;
    }

    clearTimeout(pending.timeout);
    this.pendingRequests.delete(response.requestId);

    if (response.success) {
      pending.resolve(response.payload);
    } else {
      pending.reject(
        new BridgeError(
          response.error || "Unknown error",
          response.error ? undefined : "UNKNOWN_ERROR"
        )
      );
    }
  }

  private handleEvent(event: BridgeMessage): void {
    if (event.type === EventType.READY) {
      this.isReady = true;
      this.flushQueue();
    }

    if (this.onEvent) {
      this.onEvent(event);
    }
  }

  private flushQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        this.sendMessage(message).catch((error) => {
          if (this.onEvent) {
            this.onEvent({
              id: message.id,
              type: EventType.ERROR,
              payload: {
                error: error instanceof Error ? error.message : "Unknown error",
                code: error instanceof BridgeError ? error.code : undefined,
              },
              timestamp: Date.now(),
            });
          }
        });
      }
    }
  }

  private waitForReady(iframe: HTMLIFrameElement): void {
    const checkReady = () => {
      if (iframe.contentWindow) {
        const ping = createCommandMessage({ type: CommandType.PING });
        iframe.contentWindow.postMessage(ping, this.widgetTargetOrigin);
      }
    };

    iframe.addEventListener("load", checkReady);
    checkReady();

    setTimeout(() => {
      if (!this.isReady) {
        console.warn("[Bridge] READY timeout reached, forcing ready state");
        this.isReady = true;
        this.flushQueue();
      }
    }, READY_CHECK_TIMEOUT);
  }

  destroy(): void {
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = undefined;
    }

    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timeout);
      pending.reject(new BridgeError("Bridge destroyed", "BRIDGE_DESTROYED"));
    }
    this.pendingRequests.clear();

    this.messageQueue = [];
    this.isReady = false;
    this.iframe = undefined;
  }
}
